type BackendUrlConfig = { REACT_APP_BACKEND_URL: string };

function assertBackendUrlConfig(value: unknown): asserts value is BackendUrlConfig {
  if (typeof value !== 'object' || value === null || typeof (value as { REACT_APP_BACKEND_URL?: unknown }).REACT_APP_BACKEND_URL !== 'string') {
    throw new Error('config.json is malformed: missing or non-string REACT_APP_BACKEND_URL');
  }
}

/**
 * Memoised runtime config.json loader. The file is static per deployment, but
 * several callers need it at startup (backend URL + telemetry settings), so we
 * fetch it ONCE and share the result instead of hitting the network per caller.
 * Returns `null` on any load failure; failures are NOT cached, so a later call
 * can retry. Never throws.
 */
let runtimeConfigPromise: Promise<Record<string, unknown> | null> | null = null;

export const getRuntimeConfig = (): Promise<Record<string, unknown> | null> => {
  if (!runtimeConfigPromise) {
    runtimeConfigPromise = (async () => {
      const response = await fetch('/config.json', { cache: 'no-store' });
      if (!response.ok) {
        throw new Error('Failed to load config.json');
      }
      return (await response.json()) as Record<string, unknown>;
    })().catch((error) => {
      console.error('Error loading config.json:', error);
      runtimeConfigPromise = null; // don't cache failures — allow a later retry
      return null;
    });
  }
  return runtimeConfigPromise;
};

export const getBackendUrl = async (): Promise<string> => {
  try {
    const config = await getRuntimeConfig();
    assertBackendUrlConfig(config);
    return config.REACT_APP_BACKEND_URL;
  } catch (error) {
    console.error('Error loading backend URL from config.json:', error);
    // Fallback to the dynamic URL generation if config.json is not
    // available, fails to load, or doesn't contain a usable URL.
    const frontendDomain = window.location.hostname;
    return `https://api.${frontendDomain}`;
  }
};

export type TelemetryConfig = {
  enabled: boolean;
  /** logs ingest endpoint, derived from the backend URL */
  endpoint: string;
  /**
   * OTLP/HTTP traces endpoint — the backend's /telemetry/traces proxy by
   * default (parity with logs). OTEL_TRACES_URL overrides it for local dev only.
   */
  tracesUrl: string;
  captureVerboseConsole: boolean;
  /** deployment environment (e.g. production/staging) for per-env filtering. */
  environment: string;
};

/**
 * Reads telemetry settings from the same runtime config.json (no rebuild per
 * environment). OTEL_ENABLED toggles browser logging; the ingest endpoint is
 * derived from the resolved backend URL. Defaults to disabled and never throws.
 */
export const getTelemetryConfig = async (): Promise<TelemetryConfig> => {
  const backendUrl = await getBackendUrl();
  let enabled = false;
  let captureVerboseConsole = false;
  let tracesUrl = '';
  let environment = 'development';
  try {
    // Reuse the memoised config.json (already loaded by getBackendUrl above) so
    // telemetry adds no extra network round-trip at startup.
    const config = (await getRuntimeConfig()) as {
      OTEL_ENABLED?: boolean | string;
      OTEL_CAPTURE_VERBOSE_CONSOLE?: boolean | string;
      OTEL_TRACES_URL?: string;
      OTEL_ENVIRONMENT?: string;
    } | null;
    if (config) {
      enabled = config.OTEL_ENABLED === true || config.OTEL_ENABLED === 'true';
      captureVerboseConsole = config.OTEL_CAPTURE_VERBOSE_CONSOLE === true || config.OTEL_CAPTURE_VERBOSE_CONSOLE === 'true';
      if (typeof config.OTEL_TRACES_URL === 'string') {
        tracesUrl = config.OTEL_TRACES_URL;
      }
      if (typeof config.OTEL_ENVIRONMENT === 'string' && config.OTEL_ENVIRONMENT) {
        environment = config.OTEL_ENVIRONMENT;
      }
    }
  } catch {
    // leave telemetry disabled on any config error
  }
  return {
    enabled,
    endpoint: `${backendUrl.replace(/\/$/, '')}/telemetry/logs`,
    // Send browser traces to the BACKEND (which proxies them to the Collector),
    // so the browser never contacts the Collector directly — parity with logs.
    // OTEL_TRACES_URL can still override (e.g. to post OTLP straight to a gateway).
    tracesUrl: tracesUrl || `${backendUrl.replace(/\/$/, '')}/telemetry/traces`,
    captureVerboseConsole,
    environment,
  };
};
